import { LabelPrintingLayoutOrientation } from './types';
import { LabelPrintingLayoutPreset } from './Toolbar/presets';

export function buildPrintCSS(
  layout: LabelPrintingLayoutPreset | undefined,
): string {
  const width = layout?.width || 210;
  const height = layout?.height || 297;
  const orientation = layout?.orientation;
  const marginBlock = layout?.marginBlock || 0;
  const marginInline = layout?.marginInline || 0;
  const gap = layout?.gap || 0;
  const cols = layout?.cols || 1;
  const fontSize = layout?.fontSize || 8;
  const cardHeight = layout?.cardHeight || 50;
  const heightAuto = !!layout?.heightAuto;
  const oneCardPerPage = !!layout?.oneCardPerPage;
  const withCardBorder = !!layout?.withCardBorder;
  const align = (layout?.align as string) || 'left';
  const verticalAlign = (layout?.verticalAlign as string) || 'flex-start';
  const contentCols = layout?.contentCols || 1;
  const descriptionWithLineClamp = !!layout?.descriptionWithLineClamp;

  const isLandscape = orientation === LabelPrintingLayoutOrientation.landscape;
  const pageWidth = isLandscape ? height : width;
  const pageHeight = isLandscape ? width : height;

  const printFontSize = fontSize * 0.5;

  const contentJustify =
    align === 'right'
      ? 'flex-end'
      : align === 'center'
      ? 'center'
      : 'flex-start';

  const cardHeightValue = oneCardPerPage
    ? '100%'
    : heightAuto
    ? 'auto'
    : `${cardHeight}mm`;

  const cardWidthValue = oneCardPerPage
    ? '100%'
    : cols === 1
    ? '100%'
    : `calc((100% - ${gap}mm * ${cols - 1}) / ${cols})`;

  return `
    @media print {
      body > *:not(#wrapper-label-print-host) {
        display: none !important;
      }

      #wrapper-label-print-host {
        display: block !important;
      }

      *, *::before, *::after {
        box-sizing: border-box;
      }

      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      @page {
        size: ${pageWidth}mm ${pageHeight}mm;
        margin: ${marginBlock}mm ${marginInline}mm;
      }

      .print-page-content-wrapper-paper {
        display: block !important;
        width: auto !important;
        padding: 0 !important;
        margin: 0 !important;
        background: #fff !important;
      }

      .print-page-content-paper {
        width: 100% !important;
        height: auto !important;
        ${
          oneCardPerPage
            ? `min-height: ${pageHeight - 2 * marginBlock}mm !important;`
            : ''
        }
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
        align-content: ${
          oneCardPerPage ? verticalAlign : 'flex-start'
        } !important;
        gap: ${gap}mm !important;
        font-size: ${printFontSize}mm !important;
        padding: 0 !important;
        margin: 0 !important;
        background: none !important;
        box-shadow: none !important;
        overflow: visible !important;
        ${oneCardPerPage ? 'page-break-after: always; break-after: page;' : ''}
      }

      .print-page-content-paper:last-child {
        page-break-after: auto;
        break-after: auto;
      }

      .print-page-content-card {
        width: ${cardWidthValue} !important;
        max-width: ${cardWidthValue} !important;
        flex: 0 0 ${cardWidthValue} !important;
        height: ${cardHeightValue} !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: ${verticalAlign} !important;
        text-align: ${align} !important;
        padding: 1mm !important;
        gap: 1mm !important;
        border: ${withCardBorder ? '0.2mm solid darkgray' : 'none'} !important;
        page-break-inside: avoid;
        break-inside: avoid;
        overflow: hidden !important;
      }

      .print-page-content-card > * {
        text-overflow: ellipsis !important;
        overflow: hidden !important;
        white-space: nowrap !important;
        line-height: 1 !important;
        font-weight: 500 !important;
        flex-shrink: 0 !important;
      }

      .label-print-card-description {
        font-weight: 600 !important;
        white-space: ${
          descriptionWithLineClamp ? 'nowrap' : 'pre-wrap'
        } !important;
      }

      .label-print-card-content {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
        gap: 1mm !important;
        justify-content: ${contentJustify} !important;
      }

      .label-print-card-content > * {
        width: calc((100% - 1mm * ${Math.max(
          contentCols - 1,
          0,
        )}) / ${contentCols}) !important;
        overflow: hidden !important;
      }
    }
  `;
}
